import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card } from 'primereact/card';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import {
    setFromDate,
    setToDate,
    setSelectedBranch,
    resetFilters,
    fetchCategoryFullReport,
    fetchCategoryHourlyReport,
    fetchCategoryTaxwiseReport,
    fetchCategoryPerformanceReport,
    selectActiveTab,
    selectActiveLoading,
    CATEGORY_REPORT_TABS,
    selectSelectedBranch,
    selectFromDate,
    selectToDate
} from '../../store/slices/categoryWiseReportsSlice';
import { getBranches } from '../../utilities/api';
import useUser from '../../hooks/useUser';
import './billreports.css';

const CategoryWiseReportsHeader = () => {
    const dispatch = useDispatch();
    const { userRole } = useUser();

    const selectedBranch = useSelector(selectSelectedBranch);
    const fromDate = useSelector(selectFromDate);
    const toDate = useSelector(selectToDate);
    const activeTab = useSelector(selectActiveTab);
    const loading = useSelector(selectActiveLoading);

    const role = userRole || localStorage.getItem('role') || '';
    const roleLower = role?.toLowerCase() || '';
    const isManager = roleLower === 'manager';
    const isAdmin = roleLower === 'admin';
    const isCEO = roleLower === 'ceo';
    const isGSMaster = roleLower === 'gsmaster';

    const canAccessDateRange = isAdmin || isCEO || isGSMaster;
    const canAccessBranch = isAdmin || isCEO || isGSMaster;

    const [branches, setBranches] = React.useState([]);
    const [branchesLoading, setBranchesLoading] = React.useState(false);

    useEffect(() => {
        if (isManager) {
            const today = new Date();
            dispatch(setFromDate(today));
            dispatch(setToDate(today));
        }
    }, [isManager, dispatch]);

    useEffect(() => {
        if (canAccessBranch) {
            const fetchBranches = async () => {
                try {
                    setBranchesLoading(true);
                    const clientId = localStorage.getItem('clientid');
                    if (clientId) {
                        const branchList = await getBranches(clientId);
                        const branchesWithAll = [
                            { branch_id: 'All', branch_name: 'All Branches' },
                            ...(Array.isArray(branchList) ? branchList : [])
                        ];
                        setBranches(branchesWithAll);
                        if (!selectedBranch) {
                            dispatch(setSelectedBranch(branchesWithAll[0]));
                        }
                    }
                } catch (error) {
                    console.error('Error fetching branches:', error);
                    setBranches([{ branch_id: 'All', branch_name: 'All Branches' }]);
                } finally {
                    setBranchesLoading(false);
                }
            };
            fetchBranches();
        } else {
            const managerBranchId = localStorage.getItem('branchid');
            const managerBranchName = localStorage.getItem('branchname') || 'Current Branch';
            if (managerBranchId) {
                setBranches([{ branch_id: managerBranchId, branch_name: managerBranchName }]);
                if (selectedBranch?.branch_id !== managerBranchId) {
                    dispatch(setSelectedBranch({ branch_id: managerBranchId, branch_name: managerBranchName }));
                }
            }
        }
    }, [canAccessBranch, dispatch, selectedBranch?.branch_id]);

    const getBranchId = () => {
        const b = selectedBranch;
        return (b && (typeof b === 'object' ? b.branch_id : b)) || 'All';
    };

    const fetchByActiveTab = () => {
        if (!fromDate || !toDate) return;
        const branchId = getBranchId();
        if (activeTab === CATEGORY_REPORT_TABS.FULL) {
            dispatch(fetchCategoryFullReport({ fromDate, toDate, branchId }));
        } else if (activeTab === CATEGORY_REPORT_TABS.HOURLY) {
            dispatch(fetchCategoryHourlyReport({ fromDate, toDate, branchId }));
        } else if (activeTab === CATEGORY_REPORT_TABS.TAXWISE) {
            dispatch(fetchCategoryTaxwiseReport({ fromDate, toDate, branchId }));
        } else if (activeTab === CATEGORY_REPORT_TABS.PERFORMANCE) {
            dispatch(fetchCategoryPerformanceReport({ fromDate, toDate, branchId }));
        }
    };

    const handleApplyFilters = () => {
        fetchByActiveTab();
    };

    const branchId = getBranchId();
    useEffect(() => {
        if (!isManager) return;
        if (!fromDate || !toDate) return;
        fetchByActiveTab();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isManager, fromDate, toDate, branchId, activeTab]);

    const handleResetFilters = () => {
        dispatch(resetFilters());
        if (isManager) {
            const today = new Date();
            dispatch(setFromDate(today));
            dispatch(setToDate(today));
        }
    };

    return (
        <>
            <div className="mb-3">
                <h2 className="text-3xl font-bold mb-1" style={{ color: 'var(--primary-color)' }}>
                    Category Wise Report
                </h2>
                <p className="text-color-secondary mb-0">View hourly sales by category with date and branch filters</p>
            </div>

            <Card className="mb-2 sales-reports-filter-card report-filter-uniform">
                <div className="flex flex-wrap align-items-end sales-reports-filter-row">
                    {canAccessDateRange && (
                        <>
                            <div className="flex flex-column" style={{ flex: '1 1 150px', minWidth: '140px' }}>
                                <label htmlFor="fromDate">From Date <span className="text-red-500">*</span></label>
                                <Calendar id="fromDate" value={fromDate} onChange={(e) => dispatch(setFromDate(e.value))} dateFormat="dd-mm-yy" showIcon placeholder="From Date" className="w-full" showButtonBar />
                            </div>
                            <div className="flex flex-column" style={{ flex: '1 1 150px', minWidth: '140px' }}>
                                <label htmlFor="toDate">To Date <span className="text-red-500">*</span></label>
                                <Calendar id="toDate" value={toDate} onChange={(e) => dispatch(setToDate(e.value))} dateFormat="dd-mm-yy" showIcon placeholder="To Date" minDate={fromDate} className="w-full" showButtonBar />
                            </div>
                        </>
                    )}

                    {!canAccessDateRange && (
                        <div className="flex flex-column" style={{ flex: '1 1 150px', minWidth: '140px' }}>
                            <label htmlFor="date">Date</label>
                            <Calendar id="date" value={fromDate} readOnly dateFormat="dd-mm-yy" showIcon className="w-full" disabled />
                        </div>
                    )}

                    {canAccessBranch && (
                        <div className="flex flex-column" style={{ flex: '1 1 175px', minWidth: '155px' }}>
                            <label htmlFor="branch">Branch</label>
                            <Dropdown id="branch" value={typeof selectedBranch === 'object' ? selectedBranch?.branch_id : selectedBranch} options={branches} onChange={(e) => { const branch = e.value != null ? (branches.find(b => b.branch_id === e.value) || { branch_id: e.value }) : null; dispatch(setSelectedBranch(branch)); }} optionLabel="branch_name" optionValue="branch_id" placeholder="All Branches" className="w-full" filter showClear loading={branchesLoading} />
                        </div>
                    )}

                    {!canAccessBranch && (
                        <div className="flex flex-column" style={{ flex: '1 1 175px', minWidth: '155px' }}>
                            <label htmlFor="branch">Branch</label>
                            <Dropdown id="branch" value={selectedBranch} options={branches} disabled optionLabel="branch_name" className="w-full" />
                        </div>
                    )}

                    {canAccessDateRange && (
                        <div className="flex gap-2" style={{ flex: '0 0 auto' }}>
                            <Button label="Apply" icon="pi pi-check" className="p-button-primary" onClick={handleApplyFilters} disabled={(!fromDate || !toDate) || loading} loading={loading} />
                            <Button icon="pi pi-refresh" className="p-button-secondary" onClick={handleResetFilters} tooltip="Reset" />
                        </div>
                    )}
                </div>
            </Card>
        </>
    );
};

export default CategoryWiseReportsHeader;

